// /////////////////////////////////////////////////////////////
// [ファイル] buttonStyles.ts
// [概要] プロジェクト共通のアクションボタン用 sx 定義。
//        .agents/rules/rules.md の「ボタンスタイルの標準化」ルールに従い、
//        画面横断で同種のボタンは原則これらの sx を再利用すること。
// /////////////////////////////////////////////////////////////
import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material';

// 型注釈を SystemStyleObject<Theme> にすることで、呼出側で
// `sx={[STANDARD_..., { mr: 3 }]}` のような配列マージや
// `sx={{ ...STANDARD_..., mr: 3 }}` のスプレッドが可能となる。
type SxObject = SystemStyleObject<Theme>;

// 標準アクションボタン (CTA) — Button 用
// 保存・編集・登録などの主要操作で使用する。グレー背景 →
// ホバー時にブルーグレーへ反転する統一スタイル。
export const STANDARD_CTA_BUTTON_SX: SxObject = {
  height: '28px',
  px: 3,
  fontSize: '14px',
  bgcolor: '#eeeeee',
  color: '#444444',
  boxShadow: 2,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    bgcolor: '#607d8b',
    color: '#ffffff',
    transition: '0.2s',
  },
  '&.Mui-disabled': {
    bgcolor: '#eeeeee',
    color: 'rgba(0,0,0,0.26)',
  },
};

// 標準アクションボタン (CTA) — Fab variant="extended" 用
// リスト画面ヘッダーの 検索 / 新規登録 など、ピル型ボタンで使用。
// アイコンを伴う前提で SvgIcon の余白も含めてある。
export const STANDARD_FAB_CTA_SX: SxObject = {
  height: '24px',
  minHeight: '24px',
  px: 2,
  fontSize: '12px',
  bgcolor: '#eeeeee',
  color: '#444444',
  boxShadow: 2,
  borderRadius: 3,
  textTransform: 'none',
  '&:hover': {
    bgcolor: '#607d8b',
    color: '#ffffff',
    transition: '0.2s',
  },
  '& .MuiSvgIcon-root': {
    mr: 0.75,
    fontSize: 16,
  },
};

// コンパクト版 CTA — Button 用
// カード内ヘッダーなど省スペースな場所の主要操作で使用する。
// STANDARD_CTA_BUTTON_SX と同じ配色で、寸法のみ一回り小さくする。
export const STANDARD_CTA_BUTTON_COMPACT_SX: SxObject = {
  ...STANDARD_CTA_BUTTON_SX,
  height: '24px',
  px: 1.5,
  fontSize: '12px',
  '& .MuiSvgIcon-root': {
    fontSize: 15,
  },
};

// 標準テキストボタン (Secondary)
// キャンセル・閉じる・戻る など副次操作に使用する。
// 背景なし、灰色テキストでシンプルに表示。
export const STANDARD_TEXT_BUTTON_SX: SxObject = {
  color: 'text.secondary',
  textTransform: 'none',
};

// 破壊的アクションボタン (Danger) — Button 用
// 削除・取り消しなど、後戻りできない操作の確認ダイアログで使用する。
// STANDARD_CTA_BUTTON_SX と同じ寸法で、配色のみ赤系に切り替える。
export const STANDARD_DANGER_BUTTON_SX: SxObject = {
  height: '28px',
  px: 3,
  fontSize: '14px',
  bgcolor: '#f5f5f5',
  color: '#c62828',
  boxShadow: 2,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    bgcolor: '#c62828',
    color: '#ffffff',
    transition: '0.2s',
  },
  '&.Mui-disabled': {
    bgcolor: '#f5f5f5',
    color: 'rgba(0,0,0,0.26)',
  },
};

// インライン編集の鉛筆 — IconButton 用 (components/InlineEditField)
// 普段は隠し、項目へのホバー・キーボードのフォーカスで表示する。ホバーできないタッチ端末では薄く常時表示する。
// 表示の切り替えは InlineEditField 側の親要素の sx で行う (クラス名 inline-edit-btn)。
export const INLINE_EDIT_ICON_BUTTON_SX: SxObject = {
  p: '2px',
  ml: 0.5,
  color: 'text.secondary',
  opacity: 0,
  transition: 'opacity 0.15s',
  '& .MuiSvgIcon-root': {
    fontSize: 15,
  },
  '&:hover, &:focus-visible': {
    bgcolor: '#eceff1',
    color: '#607d8b',
    opacity: 1,
  },
  '@media (hover: none)': {
    opacity: 0.35,
  },
};

// インライン編集の保存 (✓) — IconButton 用。CTA と同じブルーグレーで反転する
export const INLINE_EDIT_SAVE_BUTTON_SX: SxObject = {
  p: '3px',
  color: '#607d8b',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '&:hover': {
    bgcolor: '#607d8b',
    color: '#ffffff',
  },
};

// インライン編集の取り消し (✗) — IconButton 用。副次操作なので灰色
export const INLINE_EDIT_CANCEL_BUTTON_SX: SxObject = {
  p: '3px',
  color: 'text.secondary',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '&:hover': {
    bgcolor: '#eeeeee',
  },
};

// コンパクト版 Danger — Button 用
// 処理中の停止など、省スペースな場所で中断系の操作に使用する。
// STANDARD_DANGER_BUTTON_SX と同じ配色で、寸法は STANDARD_CTA_BUTTON_COMPACT_SX に揃える。
export const STANDARD_DANGER_BUTTON_COMPACT_SX: SxObject = {
  ...STANDARD_DANGER_BUTTON_SX,
  height: '24px',
  px: 1.5,
  fontSize: '12px',
  '& .MuiSvgIcon-root': {
    fontSize: 15,
  },
};
